/**
 * The migration flow, one decision per card.
 *
 * Five stages — wallet, eligibility, permission, migration, proof — and the
 * card only ever shows one. Contract addresses stay bundle/config authorities
 * and every guard from the previous portal survives untouched; what changed is
 * that the reader now steps forward deliberately at each boundary instead of
 * meeting an amount field the moment the balance loads.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  formatEther,
  formatUnits,
  parseUnits,
  type Address,
  type Hex,
} from "viem";

import { browserPublicClient } from "@/lib/chain.client";
import { checkClaimContract, checkLegacyToken } from "@/lib/claim-guards";
import { erc20Abi, swapAbi } from "@/lib/contracts";
import {
  ASETQU_TOKEN,
  CHAIN_ID,
  CLAIM_CONTRACT,
  LEGACY_TOKEN,
  NETWORK_NAME,
  TOKEN_DECIMALS,
  TOKEN_SYMBOL,
  explorerTx,
} from "@/lib/config";
import { describeTxError } from "@/lib/errors";
import {
  MIGRATION_PENDING_NOTICE,
  migrationActionsAllowed,
  migrationState,
  type MigrationInfo,
} from "@/lib/migration-state";
import { formatDisplay, formatTimestamp, shortenAddress } from "@/lib/format";
import { walletGasLimit } from "@/lib/gas-sponsor";
import { GasDiagnostics } from "@/components/GasDiagnostics";
import { useGasSponsorship } from "@/lib/use-gas-sponsorship";

import { ConnectButton } from "./ConnectButton";
import { useWallet } from "./WalletProvider";
import { CspImage } from "./CspImage";
import {
  ArrowDownIcon,
  AsetquMark,
  CheckIcon,
  ClockIcon,
  ExternalIcon,
  PowerIcon,
  MigrateIcon,
  PermissionIcon,
  ShieldCheckIcon,
  WalletIcon,
} from "./icons";
import { Alert, Button, CopyAction, LedgerRow, ReviewRow } from "./ui";

type SwapInfo = {
  isDenied: boolean;
  legacyBalanceRaw: string;
  allowanceRaw: string;
  walletCapRaw: string;
  alreadySwappedRaw: string;
  remainingCapRaw: string;
  maxSwappableRaw: string;
};

type Eligibility = {
  address: string;
  claimMode: "swap";
  eligible: boolean;
  claimable: boolean;
  claimPaused: boolean | null;
  claimWindow: { start: string; end: string } | null;
  /* The chain's own clock, and whether it falls inside the window. Both come
     from the API rather than the browser: AsetquSwap compares against
     block.timestamp, and a device with a wrong clock must not be told the
     window is open when the contract will revert. */
  windowOpen: boolean | null;
  chainTimestamp: string | null;
  remainingRaw: string | null;
  currentBalanceRaw: string | null;
  /* Where the migration as a whole stands, independent of this wallet: the
     launch control, the pool, and why an action is unavailable. Carried on this
     response rather than fetched separately so the page cannot show one answer
     while meaning the other. */
  migration: MigrationInfo | null;
  contracts: {
    claim: string | null;
    asetquToken: string | null;
    legacyToken: string | null;
  };
  swap: SwapInfo | null;
  chainError: string | null;
};

type Phase =
  | "idle"
  | "loading"
  | "estimating-approve"
  | "signing-approve"
  | "pending-approve"
  | "estimating-swap"
  | "signing-swap"
  | "pending-swap"
  | "done"
  | "error";

const STAGE_ICON = {
  1: WalletIcon,
  2: ShieldCheckIcon,
  3: PermissionIcon,
  4: MigrateIcon,
  5: ShieldCheckIcon,
} as const;

const STAGE_NAME = {
  1: "WALLET",
  2: "KELAYAKAN",
  3: "IZIN TOKEN",
  4: "MIGRASI",
  5: "BUKTI",
} as const;

async function fetchEligibility(target: Address): Promise<Eligibility | null> {
  try {
    const response = await fetch(`/api/eligibility/${target}`, {
      cache: "no-store",
    });
    return (await response.json()) as Eligibility;
  } catch {
    return null;
  }
}

function StageCard({
  step,
  title,
  description,
  children,
  tone = "default",
}: {
  step: 1 | 2 | 3 | 4 | 5;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  tone?: "default" | "danger";
}) {
  const StageIcon = STAGE_ICON[step];

  return (
    <section
      className={`stage-card ${tone === "danger" ? "stage-card--danger" : ""}`}
    >
      <p className="stage-card__meta">
        <StageIcon size={15} />
        <span className="stage-card__dot" aria-hidden />
        {String(step).padStart(2, "0")} / {STAGE_NAME[step]}
      </p>
      <div className="stage-card__head">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

/** The from/to pair the comps open on, before any balance is known. */
function TokenFlow({ balance }: { balance?: string }) {
  return (
    <div className="token-flow">
      <div className="token-flow__row token-flow__row--from">
        <p className="token-flow__label">Dari</p>
        <p className="token-flow__aside">Saldo: {balance ?? "—"}</p>
        <p className="token-flow__token">
          <AsetquMark />
          AsetQu Legacy
        </p>
      </div>
      <span className="token-flow__pivot" aria-hidden>
        <ArrowDownIcon size={16} />
      </span>
      <div className="token-flow__row token-flow__row--to">
        <p className="token-flow__label">Ke</p>
        <p className="token-flow__aside">Rasio 1 : 1</p>
        <p className="token-flow__token">
          <CspImage
            src="/asetqu.png"
            alt=""
            width={36}
            height={36}
            loading="eager"
          />
          AsetQu
        </p>
      </div>
    </div>
  );
}

/**
 * The connected wallet, and the way back out of it.
 *
 * The disconnect action lives here rather than in a header because there is no
 * header any more: from stage two onwards this panel is the only place the
 * portal names the wallet it is about to ask for a signature, so it is also
 * where someone who picked the wrong one has to be able to say so. Disconnect
 * drops the flow back to stage one, where RainbowKit's own list offers every
 * wallet again — the shortest honest path to "use a different one".
 */
function WalletIdentity({ address }: { address: Address }) {
  const { disconnect } = useWallet();

  return (
    <div className="wallet-identity">
      <div className="wallet-identity__head">
        <p>Wallet connected</p>
        <strong>{NETWORK_NAME}</strong>
      </div>
      <code>{address}</code>
      <div className="wallet-identity__actions">
        <CopyAction value={address} label="Salin alamat" />
        <button type="button" className="copy-action" onClick={disconnect}>
          <PowerIcon size={14} /> Ganti wallet
        </button>
      </div>
    </div>
  );
}

/**
 * Below this, a wallet is treated as having nothing.
 *
 * Only ever used to choose which sentence to show. The real decision is made
 * per transaction, against a live gas estimate and a live gas price, by the
 * server — so a wallet on the wrong side of this line is not mis-served, it is
 * merely shown the other paragraph.
 *
 * 300.000 gas at 0.1 gwei: comfortably above the measured cost of a migration
 * on BSC, and far below anything a person would think of as a balance.
 */
const GAS_DISPLAY_FLOOR = 300_000n * 100_000_000n;

/**
 * What the portal promises about gas, in the two forms the plan specifies.
 *
 * Never "free BNB", never "claim BNB". What is on offer is that the company
 * pays the cost of migrating — the wording matters because the other wording is
 * what every airdrop scam in this market says.
 */
function GasCoverageNote({
  available,
  balanceWei,
  standing,
}: {
  available: boolean;
  balanceWei: bigint | null;
  standing: {
    eligibleForSponsoredGas: boolean;
    snapshotBlock: number;
  } | null;
}) {
  if (!available) return null;

  /*
    A wallet outside the eligibility snapshot is told plainly, and told the one
    thing that matters to it: migration is unaffected.

    Said before anything is offered rather than after a refused request. The
    programme pays gas for the addresses that held legacy AsetQu at a fixed past
    block; anyone else migrates exactly as they would have, with their own BNB.
  */
  if (standing && !standing.eligibleForSponsoredGas) {
    return (
      <Alert tone="info" title="Migrasi tetap berjalan normal">
        Anda tetap dapat bermigrasi seperti biasa, tetapi gas yang ditanggung
        perusahaan tidak tersedia untuk wallet ini. Sponsorship gas hanya
        berlaku untuk alamat yang memegang AsetQu Legacy pada block{" "}
        {standing.snapshotBlock.toLocaleString("id-ID")}.
      </Alert>
    );
  }

  const needsFunding = balanceWei !== null && balanceWei < GAS_DISPLAY_FLOOR;

  return (
    <Alert tone="info" title="Biaya gas dapat ditanggung AsetQu">
      {needsFunding
        ? "Kami akan menyediakan gas minimum yang diperlukan untuk proses migrasi."
        : "Gunakan BNB Anda untuk menyelesaikan transaksi. Biaya gas yang memenuhi " +
          "syarat akan dikembalikan setelah migrasi berhasil."}
    </Alert>
  );
}

/**
 * What sponsorship is doing right now.
 *
 * Rendered only when there is something to say. An idle sponsor is not a state
 * worth a paragraph, and the migration card is already dense.
 */
function SponsorshipState({
  phase,
  message,
}: {
  phase: string;
  message: string | null;
}) {
  switch (phase) {
    case "signing":
      return (
        <div className="wallet-waiting" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>Menunggu tanda tangan</strong>
            <p>
              Tanda tangan ini hanya membuktikan kepemilikan wallet. Tidak ada
              token yang berpindah.
            </p>
          </div>
        </div>
      );
    case "sending":
      return (
        <div className="wallet-waiting" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>Menyiapkan gas</strong>
            <p>Portal sedang mengirim gas minimum ke wallet Anda.</p>
          </div>
        </div>
      );
    case "waiting-for-funds":
      return (
        <div className="wallet-waiting" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>Menunggu konfirmasi jaringan</strong>
            <p>
              {message ??
                "Gas sedang dikonfirmasi. Ini biasanya butuh beberapa detik."}
            </p>
          </div>
        </div>
      );
    case "reimbursing":
      return (
        <div className="wallet-waiting" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>Mengembalikan biaya gas</strong>
            <p>
              Portal memverifikasi transaksi Anda di jaringan sebelum mengganti
              biayanya.
            </p>
          </div>
        </div>
      );
    case "reimbursed":
      return (
        <Alert tone="ok" title="Biaya gas dikembalikan">
          Penggantian biaya gas sudah dikirim ke wallet Anda.
        </Alert>
      );
    case "funded":
      return (
        <Alert tone="ok" title="Gas sudah tersedia">
          {message ??
            "Gas untuk tahap ini sudah ada di wallet Anda. Lanjutkan transaksi."}
        </Alert>
      );
    case "unavailable":
      return message ? (
        <Alert tone="warn" title="Sponsorship gas tidak tersedia">
          {message} Anda tetap dapat melanjutkan migrasi dengan BNB sendiri.
        </Alert>
      ) : null;
    default:
      return null;
  }
}

/**
 * Where the migration window stands, decided by the chain's clock.
 *
 * Three states and each one is a different sentence, because "the button is
 * disabled" is not an explanation. Somebody arriving before the window opens
 * needs a date; somebody arriving after it closes needs to be told plainly
 * rather than left to conclude the site is broken.
 *
 * `now` is the timestamp the chain reported, not the device's. `AsetquSwap`
 * compares against `block.timestamp`, so a phone with a wrong clock would
 * otherwise be told the window is open while the contract reverts — and the
 * cost of that lands as gas spent on a transaction that could never succeed.
 *
 * Times are rendered in WIB, which is the timezone this portal has always
 * displayed and the one the window boundaries are set against.
 */
function MigrationWindow({
  window,
  open,
  now,
}: {
  window: { start: string; end: string };
  open: boolean | null;
  now: string | null;
}) {
  const start = BigInt(window.start);
  const end = BigInt(window.end);

  /*
    No fallback to the browser's clock, deliberately.

    A device clock wrong in the wrong direction would tell somebody the window
    is open while the contract reverts, and they would pay gas to find out. When
    the chain's clock is unavailable the portal says nothing about the window
    rather than guessing — the button is governed by `claimable`, which the
    server derives from that same chain clock.
  */
  if (now === null && open === null) return null;

  const chainNow = now !== null ? BigInt(now) : null;
  const beforeStart = open === false && chainNow !== null && chainNow < start;
  const afterEnd = open === false && (chainNow === null || chainNow > end);

  if (beforeStart) {
    return (
      <Alert tone="info" title="Periode migrasi belum dimulai">
        Migrasi dibuka {formatTimestamp(start)} WIB dan ditutup{" "}
        {formatTimestamp(end)} WIB. Anda dapat menghubungkan wallet sekarang
        untuk memeriksa kelayakan, tetapi transaksi belum dapat dilakukan.
      </Alert>
    );
  }

  if (afterEnd) {
    return (
      <Alert tone="danger" title="Periode migrasi telah berakhir">
        Migrasi ditutup {formatTimestamp(end)} WIB. Kontrak menolak transaksi
        baru, jadi jangan menandatangani apa pun yang mengaku masih dapat
        memigrasikan token Anda.
      </Alert>
    );
  }

  return (
    <p className="deadline-note">
      <ClockIcon size={13} />
      Periode migrasi berlangsung sampai {formatTimestamp(end)} WIB
    </p>
  );
}

export function MigrationCard({ onStep }: { onStep?: (step: number) => void }) {
  const {
    address,
    isCorrectNetwork,
    error: walletError,
    networkSwitchUrl,
    switchNetwork,
    switchingNetwork,
    disconnect,
  } = useWallet();

  const connected = Boolean(address) && isCorrectNetwork;

  useEffect(() => {
    if (!connected) onStep?.(1);
  }, [connected, onStep]);

  if (!address) {
    return (
      <StageCard
        step={1}
        title="Hubungkan wallet"
        description="Hubungkan wallet yang menyimpan AsetQu Legacy. Portal hanya membaca alamat dan saldo publik."
      >
        <TokenFlow />
        <hr className="stage-card__divider" />
        <div className="wallet-stage">
          <div className="wallet-stage__status">
            <p>Status</p>
            <strong>Belum terhubung</strong>
          </div>
          <ConnectButton />
        </div>
      </StageCard>
    );
  }

  if (!isCorrectNetwork) {
    return (
      <StageCard
        step={1}
        title="Network change required"
        description={`Wallet terhubung, tetapi migrasi hanya berlangsung di ${NETWORK_NAME}.`}
      >
        <dl className="ledger">
          <LedgerRow label="Wallet">
            <code>{shortenAddress(address, 10, 8)}</code>
          </LedgerRow>
          <LedgerRow label="Jaringan yang diperlukan" accent>
            {NETWORK_NAME}
          </LedgerRow>
          <LedgerRow label="Chain ID">{CHAIN_ID}</LedgerRow>
        </dl>
        {walletError ? (
          <Alert tone="warn" title="Wallet belum berpindah">
            {walletError}
          </Alert>
        ) : null}
        <div className="transaction-actions">
          {networkSwitchUrl ? (
            <a
              className="ui-button ui-button--primary ui-button--block"
              href={networkSwitchUrl}
            >
              Buka di Trust Wallet
            </a>
          ) : (
            <Button block onClick={switchNetwork} disabled={switchingNetwork}>
              {switchingNetwork ? "Menunggu wallet…" : "Pindah ke BNB Chain"}
            </Button>
          )}
          {/* A wallet that cannot switch chains is not a dead end: disconnecting
              is the other way out, and without this the only one is a reload. */}
          <Button block variant="secondary" onClick={disconnect}>
            <PowerIcon size={14} /> Ganti wallet
          </Button>
        </div>
      </StageCard>
    );
  }

  return <MigrationPanel key={address} address={address} onStep={onStep} />;
}

function MigrationPanel({
  address,
  onStep,
}: {
  address: Address;
  onStep?: (step: number) => void;
}) {
  const { getWalletClient, getProvider, disconnect } = useWallet();
  /* Gas sponsorship. Every failure inside it reduces to "pay your own gas",
     which is what this card did before it existed — so nothing below treats an
     unavailable sponsor as a reason not to migrate. */
  const sponsorship = useGasSponsorship(address, getProvider);

  const [eligibility, setEligibility] = useState<Eligibility | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [gasEstimate, setGasEstimate] = useState<string | null>(null);
  /* Kept for the staging diagnostic panel: the figures the portal priced this
     transaction with, so they can be put next to the wallet's own. */
  const [portalGas, setPortalGas] = useState<{
    estimate: bigint;
    priceWei: bigint;
  } | null>(null);
  const [txHash, setTxHash] = useState<Hex | null>(null);
  /* Kept after the approval confirms rather than cleared with `txHash`. The
     approval is a reimbursable transaction in its own right — the legacy token
     has no `permit`, so it is gas nobody could have avoided — and by the time
     the migration succeeds the hash is the only way to name it. */
  const [approveTxHash, setApproveTxHash] = useState<Hex | null>(null);
  const [confirmedBlock, setConfirmedBlock] = useState<bigint | null>(null);
  const [confirmedTimestamp, setConfirmedTimestamp] = useState<bigint | null>(
    null,
  );
  const [amountInput, setAmountInput] = useState("");
  /* Stage 2 is a reading stage. Nothing signs from it, and the amount field
     only appears once the reader has said they are done reading. */
  const [reviewed, setReviewed] = useState(false);

  const refresh = useCallback(async () => {
    const data = await fetchEligibility(address);
    if (data) {
      setEligibility(data);
      return data;
    }
    return null;
  }, [address]);

  useEffect(() => {
    let cancelled = false;

    void fetchEligibility(address).then((data) => {
      if (cancelled) return;
      if (data) {
        setEligibility(data);
        setPhase("idle");
        if (data.swap) {
          setAmountInput(
            formatUnits(BigInt(data.swap.maxSwappableRaw), TOKEN_DECIMALS),
          );
        }
      } else {
        setPhase("error");
        setMessage(
          "Portal tidak dapat membaca jaringan. Belum ada transaksi yang dikirim.",
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [address]);

  const amountRaw = useMemo<bigint | null>(() => {
    if (amountInput.trim() === "") return null;
    try {
      const parsed = parseUnits(amountInput.trim(), TOKEN_DECIMALS);
      return parsed > 0n ? parsed : null;
    } catch {
      return null;
    }
  }, [amountInput]);

  const swap = eligibility?.swap;
  const maxSwappable = swap ? BigInt(swap.maxSwappableRaw) : null;
  const allowance = swap ? BigInt(swap.allowanceRaw) : null;
  const amountExceedsMax =
    amountRaw !== null && maxSwappable !== null && amountRaw > maxSwappable;
  const needsApproval =
    amountRaw !== null && allowance !== null ? allowance < amountRaw : null;
  const amountLabel = amountRaw === null ? "0" : formatDisplay(amountRaw);
  const isDenied = swap?.isDenied ?? false;
  const hasBalance = maxSwappable !== null && maxSwappable > 0n && !isDenied;

  /*
    Where the migration stands, as one value rather than five booleans.

    `connectedChainId` is null because being on the wrong network is handled a
    level up: this component only renders once the wallet is on the right chain,
    and passing the id here would re-decide something already decided.

    `actionsAllowed` is what every button below is disabled on. It is the page
    being polite about a decision made elsewhere — the server's `claimable`
    folds in the same conditions, the gas routes refuse independently, and
    swap() reverts regardless of what this file believes.
  */
  const state = migrationState({
    migration: eligibility?.migration ?? null,
    chainError: eligibility?.chainError ?? null,
    claimPaused: eligibility?.claimPaused ?? null,
    windowOpen: eligibility?.windowOpen ?? null,
    chainTimestamp: eligibility?.chainTimestamp ?? null,
    claimWindow: eligibility?.claimWindow ?? null,
    connectedChainId: null,
  });
  const actionsAllowed =
    migrationActionsAllowed(state) && (eligibility?.claimable ?? false);
  const migrationPending = state === "PORTAL_READY_MIGRATION_PENDING";

  const step: 1 | 2 | 3 | 4 | 5 =
    phase === "done"
      ? 5
      : !reviewed || !hasBalance
        ? 2
        : needsApproval === false
          ? 4
          : 3;

  useEffect(() => {
    onStep?.(step);
  }, [onStep, step]);

  const runApprove = useCallback(async () => {
    if (!eligibility?.swap || amountRaw === null || !CLAIM_CONTRACT) return;
    setMessage(null);
    setTxHash(null);
    setPhase("estimating-approve");
    try {
      const legacyProblem = checkLegacyToken(
        LEGACY_TOKEN,
        eligibility.contracts.legacyToken,
      );
      if (legacyProblem) throw new Error(legacyProblem);
      const contractProblem = checkClaimContract(
        CLAIM_CONTRACT,
        eligibility.contracts.claim,
      );
      if (contractProblem) throw new Error(contractProblem);
      if (!LEGACY_TOKEN)
        throw new Error("Kontrak token legacy belum dikonfigurasi.");

      const walletClient = getWalletClient();
      const { request } = await browserPublicClient.simulateContract({
        address: LEGACY_TOKEN,
        abi: erc20Abi,
        functionName: "approve",
        args: [CLAIM_CONTRACT, amountRaw],
        account: address,
      });
      const gas = await browserPublicClient.estimateContractGas({
        address: LEGACY_TOKEN,
        abi: erc20Abi,
        functionName: "approve",
        args: [CLAIM_CONTRACT, amountRaw],
        account: address,
      });
      const gasPrice = await browserPublicClient.getGasPrice();
      setGasEstimate(`${Number(formatEther(gas * gasPrice)).toFixed(6)} BNB`);
      setPortalGas({ estimate: gas, priceWei: gasPrice });

      /* Before the wallet is opened, not after.
         A wallet that cannot pay for this transaction should never see a
         signature prompt: it would sign, the wallet would refuse to broadcast,
         and the failure would read as the portal's. So the shortfall is covered
         first, and if it cannot be, the card says so instead of prompting. */
      const covered = await sponsorship.ensureGas(gas, gasPrice);
      if (!covered.ok) {
        setPhase("idle");
        setMessage(covered.message);
        return;
      }

      setPhase("signing-approve");
      /* The fee envelope is stated, not left to the wallet.
         A wallet asked to price this itself picks a gas limit and a gas price
         the portal cannot predict — chain 97's nodes quote gas ten times apart
         for the same block, and the fee-history percentiles a wallet falls back
         to disagree with all of them. Sponsorship then funds one number while
         the wallet reserves another, and the user meets the difference as a
         greyed-out confirm button. Sending the limit and the price makes the
         wallet's maximum exactly `gasLimit × gasPrice`, which is what the
         server just funded. */
      const hash = await walletClient.writeContract({
        address: LEGACY_TOKEN,
        abi: erc20Abi,
        functionName: "approve",
        args: [CLAIM_CONTRACT, amountRaw],
        chain: request.chain,
        account: address,
        // Legacy pricing, explicitly. Chain 97 reports baseFeePerGas 0, so a
        // 1559 envelope buys nothing here and costs a wallet the fee-market
        // guesswork this is meant to remove.
        type: "legacy",
        gas: walletGasLimit(gas, sponsorship.status?.walletModel),
        gasPrice,
      });
      setTxHash(hash);
      setApproveTxHash(hash);
      setPhase("pending-approve");

      const receipt = await browserPublicClient.waitForTransactionReceipt({
        hash,
      });
      if (receipt.status === "success") {
        await refresh();
        setPhase("idle");
        setTxHash(null);
        setGasEstimate(null);
      } else {
        setPhase("error");
        setMessage(
          "Izin token gagal dikonfirmasi. Tidak ada token yang berpindah.",
        );
      }
    } catch (error) {
      setPhase("error");
      setMessage(describeTxError(error));
    }
  }, [address, amountRaw, eligibility, getWalletClient, refresh, sponsorship]);

  const runSwap = useCallback(async () => {
    if (amountRaw === null || !CLAIM_CONTRACT || !eligibility) return;
    setMessage(null);
    setPhase("estimating-swap");
    try {
      const contractProblem = checkClaimContract(
        CLAIM_CONTRACT,
        eligibility.contracts.claim,
      );
      if (contractProblem) throw new Error(contractProblem);

      const walletClient = getWalletClient();
      const { request } = await browserPublicClient.simulateContract({
        address: CLAIM_CONTRACT,
        abi: swapAbi,
        functionName: "swap",
        args: [amountRaw],
        account: address,
      });
      const gas = await browserPublicClient.estimateContractGas({
        address: CLAIM_CONTRACT,
        abi: swapAbi,
        functionName: "swap",
        args: [amountRaw],
        account: address,
      });
      const gasPrice = await browserPublicClient.getGasPrice();
      setGasEstimate(`${Number(formatEther(gas * gasPrice)).toFixed(6)} BNB`);
      setPortalGas({ estimate: gas, priceWei: gasPrice });

      const covered = await sponsorship.ensureGas(gas, gasPrice);
      if (!covered.ok) {
        setPhase("idle");
        setMessage(covered.message);
        return;
      }

      setPhase("signing-swap");
      // Same envelope as the approval, for the same reason.
      const hash = await walletClient.writeContract({
        address: CLAIM_CONTRACT,
        abi: swapAbi,
        functionName: "swap",
        args: [amountRaw],
        chain: request.chain,
        account: address,
        // Same envelope as the approval, for the same reason.
        type: "legacy",
        gas: walletGasLimit(gas, sponsorship.status?.walletModel),
        gasPrice,
      });
      setTxHash(hash);
      setPhase("pending-swap");

      const receipt = await browserPublicClient.waitForTransactionReceipt({
        hash,
      });
      if (receipt.status === "success") {
        if (typeof receipt.blockNumber === "bigint") {
          setConfirmedBlock(receipt.blockNumber);
          try {
            const block = await browserPublicClient.getBlock({
              blockNumber: receipt.blockNumber,
            });
            setConfirmedTimestamp(block.timestamp);
          } catch {
            // The receipt is the source of truth; block time is supporting metadata.
          }
        }
        await refresh();
        setPhase("done");
        setGasEstimate(null);

        /* Reimbursement, after the migration and never before it.
           Fired without awaiting so the proof card renders the moment the
           receipt lands — the payback is the portal's business and the user has
           already finished theirs. Both hashes are offered because both cost
           gas; the server decides which of them it owes, refuses the ones it
           already covered up front, and pays each at most once. */
        void (async () => {
          await sponsorship.reimburse(hash);
          if (approveTxHash) await sponsorship.reimburse(approveTxHash);
        })();
      } else {
        setPhase("error");
        setMessage("Migrasi ditolak kontrak. Token legacy tidak berpindah.");
      }
    } catch (error) {
      setPhase("error");
      setMessage(describeTxError(error));
    }
  }, [
    address,
    amountRaw,
    approveTxHash,
    eligibility,
    getWalletClient,
    refresh,
    sponsorship,
  ]);

  const addTokenToWallet = useCallback(async () => {
    if (!ASETQU_TOKEN) return;
    const provider = getProvider();
    if (!provider) return;
    try {
      await provider.request({
        method: "wallet_watchAsset",
        params: {
          type: "ERC20",
          options: {
            address: ASETQU_TOKEN,
            symbol: TOKEN_SYMBOL,
            decimals: TOKEN_DECIMALS,
          },
        },
      });
    } catch {
      // The wallet can decline without changing the migration result.
    }
  }, [getProvider]);

  const setMax = useCallback(() => {
    if (!eligibility?.swap) return;
    setAmountInput(
      formatUnits(BigInt(eligibility.swap.maxSwappableRaw), TOKEN_DECIMALS),
    );
  }, [eligibility]);

  const busy = [
    "estimating-approve",
    "signing-approve",
    "pending-approve",
    "estimating-swap",
    "signing-swap",
    "pending-swap",
  ].includes(phase);

  if (phase === "loading") {
    return (
      <StageCard
        step={2}
        title="Memeriksa kontrak"
        description="Membaca saldo legacy, allowance, batas wallet, dan status jaringan."
      >
        <div className="terminal-loading" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>Reading on-chain state</strong>
            <p>Belum ada keputusan atau transaksi yang dibuat.</p>
          </div>
        </div>
      </StageCard>
    );
  }

  const chainError = eligibility?.chainError;
  const legacyBalance = swap ? BigInt(swap.legacyBalanceRaw) : 0n;
  const alreadySwapped = swap ? BigInt(swap.alreadySwappedRaw) : 0n;
  const walletCap = swap ? BigInt(swap.walletCapRaw) : 0n;
  const criticalMessage =
    message?.toLowerCase().includes("tidak cocok") ?? false;

  if (phase === "done" && txHash) {
    return (
      <StageCard
        step={5}
        title="Migrasi terkonfirmasi"
        description="Catatan on-chain untuk transaksi ini."
      >
        <div className="proof-stamp">
          <div className="proof-stamp__head">
            <span aria-hidden>
              <CheckIcon size={20} />
            </span>
            <div>
              <p>AsetQu / on-chain record</p>
              <h2>Migrasi terkonfirmasi</h2>
            </div>
          </div>
          <strong className="proof-stamp__record">
            TX {shortenAddress(txHash, 10, 8)}
          </strong>
          <dl className="review-list">
            <ReviewRow label="Wallet">
              <code>{shortenAddress(address, 10, 8)}</code>
            </ReviewRow>
            <ReviewRow label="Legacy">
              {amountLabel} {TOKEN_SYMBOL}
            </ReviewRow>
            <ReviewRow label="Diterima" emphasis>
              {amountLabel} {TOKEN_SYMBOL}
            </ReviewRow>
            <ReviewRow label="Rasio">1 : 1</ReviewRow>
            <ReviewRow label="Network">{NETWORK_NAME}</ReviewRow>
            {confirmedBlock != null ? (
              <ReviewRow label="Block">#{confirmedBlock.toString()}</ReviewRow>
            ) : null}
            {confirmedTimestamp !== null ? (
              <ReviewRow label="Waktu">
                {formatTimestamp(confirmedTimestamp)} WIB
              </ReviewRow>
            ) : null}
          </dl>
          <SponsorshipState
            phase={sponsorship.phase}
            message={sponsorship.message}
          />
          <div className="proof-stamp__actions">
            <a
              href={explorerTx(txHash)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalIcon size={14} /> Lihat transaksi
            </a>
            <CopyAction value={txHash} label="Salin hash" />
            {ASETQU_TOKEN ? (
              <button type="button" onClick={() => void addTokenToWallet()}>
                Tambahkan {TOKEN_SYMBOL} ke wallet
              </button>
            ) : null}
            <button type="button" onClick={disconnect}>
              <PowerIcon size={14} /> Ganti wallet
            </button>
          </div>
        </div>
      </StageCard>
    );
  }

  /* ------------------------------------------------------- stage 2 --- */

  if (step === 2) {
    return (
      <StageCard
        step={2}
        title="Periksa kelayakan"
        description="Hasil pemeriksaan wallet dan kontrak resmi."
        tone={isDenied ? "danger" : "default"}
      >
        <WalletIdentity address={address} />

        {chainError ? (
          <Alert tone="warn" title="Network unavailable">
            {chainError}
          </Alert>
        ) : null}

        {isDenied ? (
          <Alert tone="danger" title="Wallet dibatasi">
            Wallet ini tidak dapat menggunakan kontrak migrasi. Jangan mencoba
            menandatangani transaksi dari tautan atau kontrak lain.
          </Alert>
        ) : null}

        {!swap && !chainError ? (
          <Alert tone="warn" title="Data kelayakan belum tersedia">
            Portal belum menerima data saldo dari kontrak. Coba muat ulang
            setelah jaringan stabil.
          </Alert>
        ) : null}

        {swap && !isDenied && hasBalance ? (
          <>
            <dl className="ledger">
              <LedgerRow label="Saldo legacy">
                {formatDisplay(legacyBalance)} {TOKEN_SYMBOL}
              </LedgerRow>
              <LedgerRow label="Sudah dimigrasikan">
                {formatDisplay(alreadySwapped)} / {formatDisplay(walletCap)}
              </LedgerRow>
              <LedgerRow label="Dapat dimigrasikan" accent>
                {formatDisplay(maxSwappable ?? 0n)} {TOKEN_SYMBOL}
              </LedgerRow>
            </dl>
            <p className="ledger-note">Rasio: 1 legacy = 1 new</p>
            <GasCoverageNote
              available={sponsorship.status?.available ?? false}
              balanceWei={sponsorship.balanceWei}
              standing={sponsorship.standing}
            />
            <hr className="stage-card__divider" />
            <Button
              block
              onClick={() => setReviewed(true)}
              disabled={!actionsAllowed}
            >
              Lanjutkan ke tahap 3 (izin token)
            </Button>
          </>
        ) : null}

        {swap && !isDenied && !hasBalance ? (
          <>
            <dl className="stat-grid">
              <div>
                <dt>Saldo legacy</dt>
                <dd>
                  {formatDisplay(legacyBalance)} {TOKEN_SYMBOL}
                </dd>
              </div>
              <div>
                <dt>Dapat dimigrasikan</dt>
                <dd>
                  {formatDisplay(maxSwappable ?? 0n)} {TOKEN_SYMBOL}
                </dd>
              </div>
              <div>
                <dt>Sudah dimigrasikan</dt>
                <dd>
                  {formatDisplay(alreadySwapped)} / {formatDisplay(walletCap)}
                </dd>
              </div>
              <div>
                <dt>Rasio</dt>
                <dd>1 : 1</dd>
              </div>
            </dl>
            <hr className="stage-card__divider" />
            <Alert
              tone="danger"
              title="Tidak ada saldo legacy yang dapat dimigrasikan"
            >
              Wallet ini tidak memiliki saldo yang tersedia untuk migrasi, atau
              batas wallet sudah tercapai.
            </Alert>
          </>
        ) : null}

        {/* The PHASE A sentence. Tone is info, not warn or danger: nothing has
            gone wrong, the pool is simply not funded yet — and a red card here
            would tell a holder their migration is in trouble when it has not
            started. */}
        {migrationPending ? (
          <Alert tone="info" title={MIGRATION_PENDING_NOTICE.title}>
            {MIGRATION_PENDING_NOTICE.body}
          </Alert>
        ) : null}

        {eligibility?.claimPaused ? (
          <Alert tone="warn" title="Migrasi dijeda">
            Kontrak sedang dijeda. Tidak ada dana yang terpengaruh dan CTA
            dinonaktifkan.
          </Alert>
        ) : null}

        {message ? (
          <Alert
            tone={criticalMessage ? "danger" : "warn"}
            title="Tindakan belum selesai"
          >
            {message}
          </Alert>
        ) : null}

        {eligibility?.claimWindow ? (
          <MigrationWindow
            window={eligibility.claimWindow}
            open={eligibility.windowOpen}
            now={eligibility.chainTimestamp}
          />
        ) : null}
      </StageCard>
    );
  }

  /* --------------------------------------------------- stage 3 and 4 --- */

  const signing = step === 3;

  return (
    <StageCard
      step={signing ? 3 : 4}
      title={signing ? "Beri izin token" : "Migrasikan token"}
      description={
        signing
          ? "Tanda tangan pertama. Wallet hanya memberi kontrak izin menggunakan exact amount; token belum berpindah."
          : "Tanda tangan kedua. AsetQu Legacy dipindahkan dan wallet yang sama menerima AsetQu baru."
      }
      tone={criticalMessage ? "danger" : "default"}
    >
      <WalletIdentity address={address} />

      <div className="amount-control">
        <label htmlFor="swap-amount">Jumlah yang dimigrasikan</label>
        <div className="amount-control__field">
          <input
            id="swap-amount"
            type="text"
            inputMode="decimal"
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
            disabled={busy}
            placeholder="0.0"
          />
          <span className="amount-control__unit">{TOKEN_SYMBOL}</span>
          <Button variant="secondary" onClick={setMax} disabled={busy}>
            Maks
          </Button>
        </div>
        {amountExceedsMax ? (
          <p role="alert">
            Jumlah melebihi batas saat ini: {formatDisplay(maxSwappable ?? 0n)}{" "}
            {TOKEN_SYMBOL}.
          </p>
        ) : null}
      </div>

      {CLAIM_CONTRACT ? (
        <>
          <div className="review-block">
            <p>Transaction review</p>
            <dl className="review-list">
              <ReviewRow label="Token">AsetQu Legacy</ReviewRow>
              <ReviewRow label="Jumlah" emphasis>
                {amountLabel} {TOKEN_SYMBOL}
              </ReviewRow>
              <ReviewRow label={signing ? "Izin" : "Hasil"}>
                {signing
                  ? "Exact amount only"
                  : `${amountLabel} ${TOKEN_SYMBOL} baru`}
              </ReviewRow>
              <ReviewRow label={signing ? "Kontrak penerima izin" : "Penerima"}>
                <code>
                  {shortenAddress(signing ? CLAIM_CONTRACT : address, 10, 8)}
                </code>
              </ReviewRow>
              <ReviewRow label="Network">{NETWORK_NAME}</ReviewRow>
              <ReviewRow label="Perkiraan network fee">
                {gasEstimate ?? "Dihitung sebelum wallet dibuka"}
              </ReviewRow>
            </dl>
          </div>

          <div className="signing-check">
            <p>Check before signing</p>
            <ul>
              <li>
                <CheckIcon size={14} /> Network: {NETWORK_NAME}
              </li>
              <li>
                <CheckIcon size={14} /> Token: AsetQu Legacy
              </li>
              <li>
                <CheckIcon size={14} /> Amount: {amountLabel} {TOKEN_SYMBOL}
              </li>
              <li>
                <CheckIcon size={14} /> Contract:{" "}
                {shortenAddress(CLAIM_CONTRACT, 8, 6)}
              </li>
            </ul>
            <strong>Jangan tanda tangani jika detail di wallet berbeda.</strong>
          </div>
        </>
      ) : (
        <Alert tone="warn" title="Kontrak migrasi belum dikonfigurasi">
          Portal tidak memiliki alamat kontrak migrasi, jadi tidak ada transaksi
          yang bisa disiapkan.
        </Alert>
      )}

      {phase === "signing-approve" || phase === "signing-swap" ? (
        <div className="wallet-waiting" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>Waiting for wallet</strong>
            <p>Periksa jumlah, kontrak, dan jaringan di wallet Anda.</p>
          </div>
        </div>
      ) : null}

      {txHash ? (
        <div className="transaction-submitted" role="status" aria-live="polite">
          <p>
            {phase === "pending-approve"
              ? "Token permission submitted"
              : "Migration submitted"}
          </p>
          <strong>Menunggu konfirmasi jaringan</strong>
          <code>{txHash}</code>
          <a
            href={explorerTx(txHash)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Lihat transaksi <ExternalIcon size={13} />
          </a>
        </div>
      ) : null}

      {message ? (
        <Alert
          tone={criticalMessage ? "danger" : "warn"}
          title={
            criticalMessage ? "Transaksi diblokir" : "Tindakan belum selesai"
          }
        >
          {message}
        </Alert>
      ) : null}

      {eligibility?.claimPaused ? (
        <Alert tone="warn" title="Migrasi dijeda">
          Kontrak sedang dijeda. Tidak ada dana yang terpengaruh dan CTA
          dinonaktifkan.
        </Alert>
      ) : null}

      <GasCoverageNote
        available={sponsorship.status?.available ?? false}
        balanceWei={sponsorship.balanceWei}
        standing={sponsorship.standing}
      />
      <SponsorshipState
        phase={sponsorship.phase}
        message={sponsorship.message}
      />

      <GasDiagnostics
        address={address}
        amountRaw={amountRaw}
        status={sponsorship.status}
        portalBalanceWei={sponsorship.balanceWei}
        portalGasEstimate={portalGas?.estimate ?? null}
        portalGasPriceWei={portalGas?.priceWei ?? null}
        getProvider={getProvider}
        readPlan={sponsorship.readPlan}
      />

      {migrationPending ? (
        <Alert tone="info" title={MIGRATION_PENDING_NOTICE.title}>
          {MIGRATION_PENDING_NOTICE.body}
        </Alert>
      ) : null}

      <div className="transaction-actions">
        {signing ? (
          <Button
            block
            onClick={() => void runApprove()}
            disabled={
              !actionsAllowed ||
              amountRaw === null ||
              amountExceedsMax ||
              busy ||
              sponsorship.busy ||
              !CLAIM_CONTRACT
            }
          >
            {sponsorship.busy
              ? "Menyiapkan gas…"
              : phase === "estimating-approve"
                ? "Menghitung network fee…"
                : phase === "signing-approve"
                  ? "Periksa wallet Anda…"
                  : phase === "pending-approve"
                    ? "Izin sedang dikonfirmasi…"
                    : `Beri izin ${amountLabel} ${TOKEN_SYMBOL}`}
          </Button>
        ) : (
          <Button
            block
            onClick={() => void runSwap()}
            disabled={
              !actionsAllowed ||
              amountRaw === null ||
              amountExceedsMax ||
              busy ||
              sponsorship.busy ||
              !CLAIM_CONTRACT
            }
          >
            {sponsorship.busy
              ? "Menyiapkan gas…"
              : phase === "estimating-swap"
                ? "Menghitung network fee…"
                : phase === "signing-swap"
                  ? "Periksa wallet Anda…"
                  : phase === "pending-swap"
                    ? "Migrasi sedang dikonfirmasi…"
                    : `Migrasikan ${amountLabel} ${TOKEN_SYMBOL}`}
          </Button>
        )}
        <p>
          Wallet akan meminta tanda tangan {signing ? "pertama" : "kedua"} dari
          dua transaksi.
        </p>
      </div>

      {eligibility?.claimWindow ? (
        <MigrationWindow
          window={eligibility.claimWindow}
          open={eligibility.windowOpen}
          now={eligibility.chainTimestamp}
        />
      ) : null}

      {!signing ? (
        <p className="terminal-label terminal-label--center">
          Izin token sudah aktif untuk {amountLabel} {TOKEN_SYMBOL}
        </p>
      ) : null}
    </StageCard>
  );
}
